import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { MpsNotificationService } from '../../mps/services/mps-notification.service';
import { MpsUserService } from '../../mps/services/mps-user.service';
import { applicationRouteNames } from '../application-route-names';
import { RoleEnum } from '../enums/roles.enum';

/**
 * Guard for the payroll-export route.
 *
 * Three cases (D10, FR-018d):
 *
 * Case a — Active role is ADMIN or PAIE → pass immediately.
 *
 * Case b — PAIE role is owned but not active (nominal production path:
 *           7 of 10 payroll contacts also hold USER or MANAGER as their active
 *           role) → call PATCH /user/active-role, refresh the user store from
 *           the returned UserResource, show a visible notification (FR-019b),
 *           then return true so the navigation completes in a SINGLE pass.
 *           The store refresh does NOT re-evaluate this guard nor trigger the
 *           PaieHomeRedirectGuard (which only runs for the home route).
 *
 * Case c — Neither admin nor payroll role owner → refuse and redirect to home.
 */
export const PayrollExportGuard = () => {
  const router = inject(Router);
  const userService = inject(MpsUserService);
  const notificationService = inject(MpsNotificationService);
  const translateService = inject(TranslateService);
  const loggedInUser = userService.getUser();

  if (!loggedInUser) {
    return router.navigateByUrl(applicationRouteNames.home);
  }

  const activeRole = loggedInUser.role?.code?.toUpperCase();

  // Case a: active role is already ADMIN or PAIE — pass through.
  if (activeRole === RoleEnum.ADMIN || activeRole === RoleEnum.PAIE) {
    return true;
  }

  // Case b: user owns the PAIE role but it is not currently active.
  // Switch active role via the existing PATCH /user/active-role endpoint.
  // UserResource already exposes role (active) and roles (held) — nothing
  // to add server-side, and no middleware that would write users.role_id
  // via a GET. The switch is transparent: one navigation, the one the mail
  // link asked for.
  const paieRole = loggedInUser.roles?.find((r) => r.code?.toUpperCase() === RoleEnum.PAIE);
  if (paieRole) {
    return userService.switchActiveRole(paieRole.id).pipe(
      map(() => {
        // FR-019b: make the switch visible.
        notificationService.info(translateService.instant('pages.payroll_export.role_switched'));
        return true as const;
      }),
      catchError(() => of(router.createUrlTree([applicationRouteNames.home])))
    );
  }

  // Case c: user has no ADMIN or PAIE entitlement — refuse.
  return router.navigateByUrl(applicationRouteNames.home);
};
